import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, MapPin, CalendarDays, Quote, ArrowRight, Images, X, Camera } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import Lightbox from "@/components/Lightbox";
import { pastWorkApi, fileUrl } from "@/lib/api";

function coverOf(item) {
  if (item.cover_image) return item.cover_image;
  const img = (item.gallery || []).find((g) => g.type !== "video");
  return img ? fileUrl(img.url) : "";
}

export default function PastWork() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [lb, setLb] = useState({ items: null, index: null });

  useEffect(() => {
    pastWorkApi.listPublic().then(setItems).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const openLb = (gallery, i) => setLb({ items: gallery, index: i });

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border bg-primary px-5 py-20 text-center sm:px-8 sm:py-28">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #cfa53b 0, transparent 40%), radial-gradient(circle at 80% 60%, #cfa53b 0, transparent 35%)" }} />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative mx-auto max-w-3xl">
          <p className="eyebrow mb-3 text-gold">Our Portfolio</p>
          <h1 className="font-serif text-4xl font-semibold tracking-tight text-white sm:text-5xl lg:text-6xl">Past Work</h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-white/70">
            A glimpse into the celebrations we've crafted — real events, real stories, and the moments we helped bring to life.
          </p>
        </motion.div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        {loading ? (
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => <div key={i} className="h-80 animate-pulse rounded-3xl bg-secondary/60" />)}
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center" data-testid="past-work-empty">
            <Camera className="mx-auto h-10 w-10 text-gold" />
            <h3 className="mt-4 font-serif text-2xl font-medium">Our portfolio is coming soon</h3>
            <p className="mt-2 text-sm text-muted-foreground">We're curating highlights from our favourite events. Check back shortly.</p>
            <button onClick={() => (window.location.href = "/#plan")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
              Plan your event <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="past-work-grid">
            {items.map((item, idx) => {
              const cover = coverOf(item);
              const count = (item.gallery || []).length;
              return (
                <motion.button
                  key={item.id}
                  type="button"
                  onClick={() => setActive(item)}
                  data-testid={`past-work-card-${item.id}`}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (idx % 3) * 0.08 }}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-border bg-card text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                >
                  <div className="relative aspect-[4/3] overflow-hidden bg-secondary">
                    {cover ? (
                      <img src={cover} alt={item.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    ) : (
                      <div className="flex h-full items-center justify-center"><Images className="h-10 w-10 text-muted-foreground" /></div>
                    )}
                    <span className="absolute left-4 top-4 rounded-full bg-black/55 px-3 py-1 text-xs font-semibold text-white backdrop-blur">{item.category}</span>
                    {count > 0 && (
                      <span className="absolute bottom-4 right-4 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                        <Images className="h-3.5 w-3.5" /> {count}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-serif text-xl font-semibold leading-snug">{item.title}</h3>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      {item.event_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-gold" />{item.event_date}</span>}
                      {item.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold" />{item.location}</span>}
                    </div>
                    {item.description && <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{item.description}</p>}
                    {item.testimonial && (
                      <p className="mt-4 line-clamp-2 border-l-2 border-gold pl-3 text-sm italic text-foreground/80">"{item.testimonial}"</p>
                    )}
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
                      View gallery <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </motion.button>
              );
            })}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-secondary/40 px-5 py-16 text-center sm:px-8">
        <Sparkles className="mx-auto h-7 w-7 text-gold" />
        <h2 className="mt-4 font-serif text-3xl font-semibold">Let's create your next celebration</h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Inspired by what you see? Tell us about your event and we'll craft something unforgettable.</p>
        <Link to="/#plan" className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg">
          Plan Your Event <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* Detail modal */}
      {active && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4 py-10" onClick={() => setActive(null)} data-testid="past-work-detail">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mx-auto w-full max-w-4xl overflow-hidden rounded-3xl bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-[16/7] bg-secondary">
              {coverOf(active) ? (
                <img src={coverOf(active)} alt={active.title} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full items-center justify-center"><Images className="h-12 w-12 text-muted-foreground" /></div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <button onClick={() => setActive(null)} data-testid="past-work-detail-close" className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur hover:bg-black/60"><X className="h-5 w-5" /></button>
              <div className="absolute bottom-5 left-6 right-6 text-white">
                <span className="rounded-full bg-gold px-3 py-1 text-xs font-semibold text-primary">{active.category}</span>
                <h2 className="mt-2 font-serif text-3xl font-semibold">{active.title}</h2>
                <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/85">
                  {active.event_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" />{active.event_date}</span>}
                  {active.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{active.location}</span>}
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8">
              {active.description && (
                <div className="mb-6">
                  <p className="eyebrow mb-2 text-gold">About the event</p>
                  <p className="text-sm leading-relaxed text-foreground/80">{active.description}</p>
                </div>
              )}

              {active.testimonial && (
                <div className="mb-8 rounded-2xl bg-secondary/60 p-6">
                  <Quote className="h-6 w-6 text-gold" />
                  <p className="mt-3 font-serif text-lg italic leading-relaxed text-foreground">"{active.testimonial}"</p>
                  {active.client_name && <p className="mt-3 text-sm font-semibold text-muted-foreground">— {active.client_name}</p>}
                </div>
              )}

              {(active.gallery || []).length > 0 && (
                <div>
                  <p className="eyebrow mb-3 text-gold">Photo gallery</p>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {active.gallery.map((m, i) => (
                      <button key={m.id || i} onClick={() => openLb(active.gallery, i)} data-testid={`past-work-photo-${i}`} className="group relative aspect-square overflow-hidden rounded-xl bg-secondary">
                        {m.type === "video" ? (
                          <video src={fileUrl(m.url)} className="h-full w-full object-cover" muted />
                        ) : (
                          <img src={fileUrl(m.url)} alt={m.filename || active.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      <Lightbox items={lb.items} index={lb.index} onClose={() => setLb({ items: null, index: null })} onIndex={(i) => setLb((s) => ({ ...s, index: i }))} />
    </div>
  );
}
